import React from "react";
import { L2Selector, L3Selector } from "../../common/network/NetworkSelectors";
import { Network } from "../../services/NetworkApi";
import { useNetwork } from "../../hooks/useNetwork";
import not from "ramda/src/not";

import { MainLayout } from "../../layout/MainLayout";
import { Grid } from "../../UI/Grid";
import useAuth from "../login/AuthContext";
import { useRoommateListHook } from "./useRoommateListHook";
import API from "../../services/Api";
import { RoomSelectorsTable } from "../../components/RoomSelectorsTable";
import { Typography } from "../../UI/Typography";
import { COLOR_BLUE_BAYOUX, COLOR_STALE_GRAY } from "../../constants/styles";
import { UsersMatchFilterOptions } from "../../components/UsersMatchFilterOptions";
import { SelectedGroupsTable } from "../../components/SelectedGroupsTable";
import { RoomOptions } from "../../components/RoomOptions";
import { usePageTablePaginationContext } from "../../components/PageTablePagination/context";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useSelectedNetworkImportTypes } from "../../hooks/useSelectedNetworkImportType";

export const UsersMatchPage = () => {
  const { isAcc } = useSelectedNetworkImportTypes();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const { selectedNetwork, getL3, getL2 } = useNetwork();
  const [subnetwork, setSubnetwork] = React.useState<number | null>(null);
  const [matchLoading, setMatchLoading] = React.useState<any>(false);
  const [l3, setL3] = React.useState<number | null>(null);
  const [isUsersVisible, setUsersVisible] = React.useState(true);
  const { page, setPage } = usePageTablePaginationContext();

  const [subnetworkInfo, setSubnetworkInfo] = React.useState<any>(null);

  const { getAuthToken, isWriteAccess } = useAuth();

  const [groups, setGroups] = React.useState<any[]>([]);
  const [adminLockIn, setAdminLockIn] = React.useState(false);
  const [isOverrideGenderCheck, setIsOverrideGenderCheck] = React.useState(
    !isAcc
  );

  const {
    results,
    loading,
    filter,
    setFilter,
    loadResults,
  } = useRoommateListHook(
    selectedNetwork.l1Id,
    subnetwork,
    l3,
    page,
    groups.flatMap((group) => group.roomOccupants.map((x: any) => x.uid))
  );

  

  React.useEffect(() => {
    setGroups([]);
    setPage(1);

    if (subnetwork) {
      const l2inf = getL2(selectedNetwork.l1Id, subnetwork);
      setSubnetworkInfo(l2inf);
      setUsersVisible(true);
    } else {
      setSubnetworkInfo({});
    }
  }, [subnetwork, l3]);

  const isReadOnly = React.useMemo(() =>  !!((subnetworkInfo && (subnetworkInfo?.matchingRoundStatus === "ARCHIVED")) ), [subnetworkInfo]);
  const l2Filter = (l2: Network) => (l2.matchingRoundStatus !== "ARCHIVED");

  const removeLastAddedGroup = () => {
    setGroups(groups.slice(0, -1));

    if (groups.length === 1) {
      setUsersVisible(true);
    }
  };

  const toggleAdminLockIn = () => setAdminLockIn(not);
  const toggleOverrideGenderCheck = () => setIsOverrideGenderCheck(not);

  const sendGroupsForMatching = async () => {
    try {
      if (groups.length > 1) {
        setMatchLoading(true);
        const usersToMatch = groups
          .map((x) => x.roomOccupants)
          .flatMap((x) => x)
          .map((x) => x.uid);
        const post = await API.matchGroups(
          selectedNetwork.l1Id,
          subnetwork,
          l3,
          usersToMatch,
          adminLockIn,
          isOverrideGenderCheck,
          await getAuthToken()
        );

        if (post.error) {
          showErrorSnackbar(
            post.error?.error?.description
              ? post.error.error.description
              : "Unable to match"
          );
        } else {
          setGroups([]);
          setUsersVisible(true);
          
          const matchedUsers = groups.map((x) => x.roomOccupants)
            .flatMap((x) => x)
            .map((x) => x.name);
          
          showSuccessSnackbar("Successfully matched " + matchedUsers);
        }

        setMatchLoading(false);
      }
    } catch (error) {
      showErrorSnackbar("An error occured. Please try again");
    }
  };

  const totalInGroup = groups.flatMap((group) =>
    group.roomOccupants.map((x: any) => x.uid)
  ).length;

  let totalRoommates = 0;
  if (l3 && subnetwork) {
    const l3Info = getL3(selectedNetwork.l1Id, subnetwork, l3);

    if (l3Info) {
      totalRoommates = l3Info.size + 1;
    }
  }

  const isFullRoom = totalInGroup >= totalRoommates;
  const isApt = selectedNetwork.apt;

  const setGroup = (arg: any) => {
    setGroups(arg);
    setUsersVisible(false);
    setFilter((prevFilters) => ({
      ...prevFilters,
      name: null,
    }));
  };

  return (
    <MainLayout title="Power Match">
      {isWriteAccess() && (
        <Grid container spacing={2}>
          <Grid item container spacing={1} mb={3}>
            <Grid item>
              <L2Selector onSelectHandler={setSubnetwork} filter={l2Filter}/>
            </Grid>
            <Grid item>
              <L3Selector
                onSelectHandler={setL3}
                l2Selection={subnetwork}
                fullWidth
              />
            </Grid>
          </Grid>
          {!!groups.length && (
            <Grid item xs={12}>
              <SelectedGroupsTable
                isApt={isApt}
                groups={groups}
                reloadResults={loadResults}
                matchGroupAtLimit={results.matchGroupAtLimit}
              />
            </Grid>
          )}
          {!!l3 && !results?.rooms?.length && (
            <Grid item xs={12}>
              <Typography variant="body2" color={COLOR_BLUE_BAYOUX}>
                No groups found
              </Typography>
            </Grid>
          )}
          {!!l3 && (
            <Grid item container spacing={2}>
              {!groups.length && (
                <Grid item xs={12}>
                  <Typography
                    fontSize={12}
                    fontWeight={700}
                    color={COLOR_STALE_GRAY}
                    sx={{ textTransform: "uppercase" }}
                  >
                    Select First Group To Power Match
                  </Typography>
                </Grid>
              )}
              {!!groups.length && (
                <Grid item xs={12}>
                  <RoomOptions
                    isAdminLockIn={adminLockIn}
                    isLoading={loading}
                    isOverrideGenderCheck={isOverrideGenderCheck}
                    toggleOverrideGenderCheck={toggleOverrideGenderCheck}
                    toogleAdminLockIn={toggleAdminLockIn}
                    isMatchLoading={matchLoading}
                    isUsersVisible={isUsersVisible}
                    setUsersVisible={setUsersVisible}
                    matchGroups={sendGroupsForMatching}
                    removeLastAddedGroup={removeLastAddedGroup}
                    isNextButtonDisabled={!(results as any).total || isFullRoom || isReadOnly}
                    isMatchButtonDisabled={
                      !(
                        isFullRoom ||
                        !(results as any).total ||
                        groups.length > 1
                      ) || isReadOnly
                    }
                  />
                </Grid>
              )}
              {isUsersVisible && (
                <>
                  <Grid item xs={12}>
                    <UsersMatchFilterOptions
                      filterState={filter}
                      setFilter={setFilter}
                      l2Information={subnetworkInfo}
                      isUsersVisible={isUsersVisible}
                    />
                  </Grid>
                  {!results?.rooms?.length && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color={COLOR_BLUE_BAYOUX}>
                        No groups found
                      </Typography>
                    </Grid>
                  )}
                  {!!results?.rooms?.length && (
                    <Grid item xs={12}>
                      <RoomSelectorsTable
                        loading={loading}
                        results={results}
                        setGroup={setGroup}
                        selectedGroup={groups}
                        reloadResults={loadResults}
                        maxRoomates={totalRoommates}
                        isSelectedDisabled={isReadOnly}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </Grid>
      )}
    </MainLayout>
  );
};
